import React, { createContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';

// MUTATIONS
import CREATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/CREATE_CAMPAIGN_ORDER_REQUESTOR';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';
import UPDATE_SALESFORCE_OPPORTUNITY from '../../GraphQl/Mutations/UPDATE_SALESFORCE_OPPORTUNITY';

// QUERIES
import CURRENT_USER from '../../GraphQl/Queries/CURRENT_USER';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS';
import GET_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR';
import GET_CAMPAIGN_ORDERS_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDERS_REQUESTOR';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER';
import GET_BASIC_TAG_ACTIVITY from '../../GraphQl/Queries/GET_BASIC_TAG_ACTIVITY';
import GET_ORG_CRM_INTEGRATIONS from '../../GraphQl/Queries/GET_ORG_CRM_INTEGRATIONS';
import GET_ORG_SALESFORCE_NAME from '../../GraphQl/Queries/GET_SALESFORCE_ACCOUNT_NAME';

import { CampaignConfig } from '../../../platform/ux/Campaigns/shared/configs/campaignConfig';

const CampaignRequestorContext = createContext({});

export const CampaignRequestorProvider = (props) => {
  const { children } = props;
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [currentUserOrg, setCurrentUserOrg] = useState(null);
  const [campaignOrdersConnection, setCampaignOrdersConnection] = useState([]);
  const [kpis, setKpis] = useState(CampaignConfig.kpiDefaultOptions);
  const [tagActivityData, setTagActivityData] = useState(null);
  const [showActivityPopconfirm, setShowActivityPopconfirm] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const previewUserData = (() => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing userData from localStorage:', error);
      return null;
    }
  })();

  // QUERIES
  const [fetchChildrenOrgs, { data: dataChildrenOrgs, loading: loadingChildrenOrgs }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS, {
      onCompleted: (data) => {
      },
    });

  const [fetchSelectedAdvertiser] = useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER, {
    fetchPolicy: 'network-only', // Bypass cache to ensure latest pricingModel data
    onCompleted: ({ org }) => {
      setSelectedAdvertiser(org);
    },
  });

  const [fetchCampaignOrder, { data: dataCampaignOrder, loading: loadingCampaignOrder }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR, {
      fetchPolicy: 'network-only', // Bypass cache to ensure latest advertiser.pricingModel data
    });

  const [
    fetchCampaignOrdersConnection,
    { data: dataCampaignOrdersConnection, loading: loadingCampaignOrdersConnection },
  ] = useLazyQuery(GET_CAMPAIGN_ORDERS_REQUESTOR, {
    fetchPolicy: 'cache-and-network',
  });

  const [fetchOrgCRMIntegrations, { data: dataOrgCRMIntegrations }] = useLazyQuery(GET_ORG_CRM_INTEGRATIONS, {
    fetchPolicy: 'network-only',
  });

  const [fetchSalesforceAccountName, { data: dataSalesforceAccountName, loading: loadingSalesforceAccountName }] =
    useLazyQuery(GET_ORG_SALESFORCE_NAME, {
      fetchPolicy: 'network-only',
    });

  const [getBasicTagActivityDirect, { loading: loadingBasicTagActivity }] = useLazyQuery(GET_BASIC_TAG_ACTIVITY, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.getBasicTagActivity && data.getBasicTagActivity.retailID[0].transactions === 0) {
        setTagActivityData(data.getBasicTagActivity);
        setShowActivityPopconfirm(true);
      }
    },
  });

  const {
    data,
    loading: loadingCurrentUser,
    error: currentUserError,
  } = useQuery(CURRENT_USER, {
    onCompleted: ({ currentUser }) => {
      if (currentUser && currentUser.defaultRole) {
        const currentOrg =
          (previewUserData && previewUserData.role && previewUserData.role.org) ||
          currentUser.defaultRole.org;

        // Step 1: Immediately set advertiser so it shows up in the UI
        setSelectedAdvertiser(currentOrg);

        // Step 2: Fetch complete advertiser data including pricingModel
        // This will update selectedAdvertiser with the full data when it completes
        if (currentOrg && currentOrg.id) {
          fetchSelectedAdvertiser({
            variables: {
              where: { id: currentOrg.id }
            }
          });
        }
      }
    },
    onError: (error) => {
      console.error('Error fetching current user:', error);
    },
  });

  const currentUser = data?.currentUser;

  // MUTATIONS
  const [createCampaignOrder, { loading: loadingCreateCampaignOrder }] = useMutation(
    CREATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      onCompleted: ({ createCampaignOrderRequestor }) => {
        fetchCampaignOrder({
          variables: {
            where: {
              id: createCampaignOrderRequestor.id,
            },
          },
        });
      },
    }
  );

  const [updateCampaignOrder, { loading: loadingUpdateCampaignOrder }] = useMutation(
    UPDATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      update: (cache, { data: { updateCampaignOrderRequestor } }) => {
        cache.writeQuery({
          query: GET_CAMPAIGN_ORDER_REQUESTOR,
          variables: {
            where: {
              id: updateCampaignOrderRequestor.id,
            },
          },
          data: {
            campaignOrder: updateCampaignOrderRequestor,
          },
        });
      },
    }
  );

  const [updateSalesforceOpportunity, { loading: loadingUpdateSalesforceOpportunity }] = useMutation(
    UPDATE_SALESFORCE_OPPORTUNITY
  );

  // TODO: This should be movd to user context
  useEffect(() => {
    if (currentUser) {
      const currentOrg =
        (previewUserData && previewUserData.role && previewUserData.role.org) ||
        currentUser.defaultRole.org;
      setCurrentUserOrg(currentOrg);
      if (currentOrg && currentOrg.id) {
        fetchOrgCRMIntegrations({ variables: { where: { id: currentOrg.id } } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (dataCampaignOrdersConnection && dataCampaignOrdersConnection.campaignOrdersConnection) {
      setCampaignOrdersConnection(
        dataCampaignOrdersConnection.campaignOrdersConnection.edges.map((edge) => edge.node)
      );
      setPagination({
        ...pagination,
        total: dataCampaignOrdersConnection.full.aggregate.count,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCampaignOrdersConnection]);

  return (
    <CampaignRequestorContext.Provider
      value={{
        currentUser,
        loadingCurrentUser,
        currentUserError,
        childrenOrg: dataChildrenOrgs ? dataChildrenOrgs.orgs : [],
        loadingChildrenOrgs,
        selectedAdvertiser,
        currentUserOrg,
        currentCampaignOrder: dataCampaignOrder ? dataCampaignOrder.campaignOrder : null,
        loadingCreateCampaignOrder,
        loadingCampaignOrder,
        loadingUpdateCampaignOrder,
        campaignOrdersConnection,
        loadingCampaignOrdersConnection,
        pagination,
        kpis,
        tagActivityData,
        showActivityPopconfirm,
        loadingBasicTagActivity,
        setShowActivityPopconfirm,
        setKpis,
        createCampaignOrder,
        updateCampaignOrder,
        updateSalesforceOpportunity,
        loadingUpdateSalesforceOpportunity,
        setSelectedAdvertiser,
        fetchCampaignOrder,
        fetchCampaignOrdersConnection,
        setPagination,
        fetchChildrenOrgs,
        fetchSelectedAdvertiser,
        getBasicTagActivityDirect,
        orgCRMIntegrations: dataOrgCRMIntegrations ? dataOrgCRMIntegrations.org : null,
        fetchSalesforceAccountName,
        salesforceAccountName: dataSalesforceAccountName ? dataSalesforceAccountName.getOrgSalesforceName : null,
        loadingSalesforceAccountName,
      }}
      {...props}
    >
      {loadingCurrentUser ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      ) : (
        children
      )}
    </CampaignRequestorContext.Provider>
  );
};

export default CampaignRequestorContext;
